import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { observer } from 'mobx-react';
import { CircularProgress } from '@mui/material';
import { useApi } from '../../hooks/useApi';
import { useStores } from '../../hooks/useStore';
import { Button } from '../../components/UI/Button/Button';
import { SubmissionDetails, submissionStatusLabel } from '../../models/assignments';
import AnswerView from './components/AnswerView';
import GradeForm from './components/GradeForm';
import styles from './reviewSubmission.module.css';

// Форма отказа interceptor'а ApiClient при не-2xx ответе.
interface ApiError {
  success: false;
  error?: string;
  code: number;
}

function isApiError(value: unknown): value is ApiError {
  return typeof value === 'object' && value !== null && 'code' in value;
}

const ReviewSubmission: React.FC = observer(() => {
  const api = useApi();
  const { notificationsStore } = useStores();
  const navigate = useNavigate();
  const { submissionId } = useParams();

  const [submission, setSubmission] = useState<SubmissionDetails | null>(null);
  const [saving, setSaving] = useState(false);

  const load = async (): Promise<void> => {
    if (!submissionId) {
      return;
    }

    try {
      notificationsStore.showPreloader();
      const resp = await api.review.get(submissionId);
      if (resp.body) {
        setSubmission(resp.body);
      }
    } catch (e) {
      if (isApiError(e) && e.code === 404) {
        notificationsStore.setError('Работа не найдена');
        navigate('/review');
        return;
      }
      notificationsStore.setError(isApiError(e) && e.error ? e.error : 'Ошибка при загрузке работы');
    } finally {
      notificationsStore.hidePreloader();
    }
  };

  useEffect(() => {
    load();
  }, [submissionId]);

  const handleGrade = async (req: { status: 'accepted' | 'rejected'; feedback: string }): Promise<void> => {
    if (!submissionId) {
      return;
    }

    try {
      setSaving(true);
      await api.review.grade(submissionId, req);
      notificationsStore.setNotifications('Работа проверена');
      navigate('/review');
    } catch (e) {
      if (isApiError(e) && e.code === 409) {
        notificationsStore.setError('Работу уже проверил другой куратор');
        await load();
        return;
      }
      notificationsStore.setError(isApiError(e) && e.error ? e.error : 'Ошибка при сохранении решения');
    } finally {
      setSaving(false);
    }
  };

  if (submission === null) {
    return (
      <div className={styles.container}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.back}>
        <Button appearance="link" onClick={() => navigate('/review')}>
          ← К очереди
        </Button>
      </div>
      <div className={styles.pageName}>
        <div className={styles.top}>
          <div className={styles.title}>
            <h2>{submission.user_name}</h2>
          </div>
        </div>
        <div className={styles.breadcrumbs}>
          {submission.course_title} › {submission.lesson_title} › {submission.assignment_title}
        </div>
        <div className={styles.meta}>
          <span>{submission.user_email}</span>
          <span>Попытка № {submission.attempt_no}</span>
          <span>Версия задания {submission.assignment_version}</span>
          <span>{submissionStatusLabel(submission.status)}</span>
          <span>Сдано: {new Date(submission.submitted_at).toLocaleString('ru-RU')}</span>
        </div>
        <p className={styles.snapshotHint}>
          Показан снимок вопросов на момент сдачи — текущая версия задания в этом ответе
          недоступна, сравнение невозможно.
        </p>
      </div>

      <div className={styles.answers}>
        {submission.answers.map((answer, index) => (
          <AnswerView key={answer.id} answer={answer} index={index} />
        ))}
      </div>

      <GradeForm
        status={submission.status}
        feedback={submission.feedback}
        gradedAt={submission.graded_at}
        disabled={saving}
        onGrade={handleGrade}
      />
    </div>
  );
});

export default ReviewSubmission;
